import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

/**
 * The cookies that keep the customer's place in the visit (card #11603): the
 * draft order (`orderUuid-<alias>`) and the location a table QR link opened
 * (`location-order*`).
 *
 * A browser that refuses cookies ("Block All Cookies" on iPhone, site data
 * blocked in Chrome) drops each write without an error, and
 * `navigator.cookieEnabled` still reports true. The order was then lost, so the
 * next existing-order check created another draft order and asked Eat-In /
 * Take-Away again; and once `#l/<label>` left the address bar a table order
 * carried on as pickup, or a table-only store fell back to its "scan the QR
 * code" gate. When a write is refused, a copy is kept in memory for the visit.
 * It never goes into the address bar, so a copied link does not carry the
 * order, and a reload starts fresh. A write the browser accepts drops any
 * copy, so for a browser that accepts cookies the cookie alone is read,
 * expires, is deleted and survives a reload, as before.
 *
 * Root-provided on purpose: `PublicModule` provides a `LocationService` of its
 * own besides the root one, and the kiosk inactivity reset clears the order and
 * the location through root services. All of them must see the same copies, as
 * they see the same cookie jar.
 */
@Injectable({
  providedIn: 'root',
})
export class VisitCookieService {
  private readonly copies = new Map<string, string>();

  constructor(private cookieService: CookieService) {}

  set(name: string, value: string, expires: number, path?: string): void {
    this.cookieService.set(name, value, expires, path);
    // A refused cookie is dropped without an error; reading it back is the
    // only way to tell.
    if (this.cookieService.get(name) === value) {
      this.copies.delete(name);
    } else {
      this.copies.set(name, value);
    }
  }

  get(name: string): string {
    // The copy first: it only exists when the latest write was refused, so it
    // is newer than any cookie still in the jar.
    return this.copies.has(name)
      ? this.copies.get(name)
      : this.cookieService.get(name);
  }

  /** Every cookie in the jar with the copies over it, each as `get` reads it. */
  getAll(): { [key: string]: string } {
    return {
      ...this.cookieService.getAll(),
      ...Object.fromEntries(this.copies),
    };
  }

  delete(name: string, path?: string): void {
    this.copies.delete(name);
    this.cookieService.delete(name, path);
  }
}
